angular.module('env.data.services.gridDataProvider', [])

    .service('gridDataProvider', function ($log, $customisationConfig, orderItemsRepository,
                                           customResourceResolver, omqlAliasBuilder, gridTextSearchService,
                                           orderItemsGridSharedSettings, toastr, gettextCatalog) {
        var _this = this;

        _.extend(_this, {
            getGridConfig: getGridConfig,
            getData: getData,
            getReportQuery: getReportQuery
        });

        return _this;

        /**
         * Get Grid configuration according to the current user and step.
         * @param {string} forStep step code of current step
         * @returns {Promise} promise of grid configuration
         */
        function getGridConfig(forStep) {
            return customResourceResolver.resolveConfig("grid-config", null, {step: forStep});
        }

        /**
         * Get data for grid according to grid filtering/sorting and additional filters
         * @returns {Promise<{items:RepresentationModel[], pageNumber: number, rowsPerPage: number, totalCount: number, totalPageCount: number}>} promise of search results object
         */
        function getData() {
            var forStep = orderItemsGridSharedSettings.getStepFilter() || "all-open";
            var whereString = buildWhereString(); //Builds where string according to the search term entered by user in 'search box' on the grid toolbar
            var sortString = buildSortString(); //Builds sort string according to the sorting applied over grid's columns headers
            var pageIndex = (orderItemsGridSharedSettings.getPageNumber() || 1) - 1; // OMQL use zero-based page number
            var pageSize = orderItemsGridSharedSettings.getPageSize() || 10;

            $log.debug("Search query for step '%s':", forStep);
            $log.debug(whereString);
            $log.debug(sortString);

            return orderItemsRepository.search({
                where: whereString,
                orderBy: sortString,
                pageNumber: pageIndex,
                rowsPerPage: pageSize
            }).then(function (resp) {

                // fix for page number and selection
                resp.pageNumber = (resp.pageNumber || 0) + 1; // OMQL use zero-based page number
                if (resp.totalCount <= 0 || resp.totalPageCount == 0) {
                    resp.pageNumber = 1;
                } else if (resp.pageNumber > resp.totalPageCount) {
                    orderItemsGridSharedSettings.setPageNumber(resp.totalPageCount);
                    orderItemsGridSharedSettings.setPageSize(resp.rowsPerPage || 10);
                    $log.debug("Making additional search request to fix page information...");
                    return getData();
                }

                orderItemsGridSharedSettings.setPageNumber(resp.pageNumber);
                orderItemsGridSharedSettings.setPageSize(resp.rowsPerPage);

                return resp;
            });
        }

        /**
         * Get query for report generators representing the current context shown by the gridDataProvider
         * @returns {{query: string, orderBy: string}} report query object
         */
        function getReportQuery() {
            return {
                query: buildWhereString(),
                orderBy: buildSortString()
            };
        }

        function buildWhereString() {
            // See if we have any 'ignored' steps in workflow config meaning we don't want to get items for those steps
            var stepsFilter = buildStepsFilterForIgnoredSteps();
            // See if we have dates supplied meaning we're about to show completed items for particular period
            var datesFilter = buildDatesFilterIfAny();
            // Text search for customisable number of fields
            var textSearchFilter = buildTextSearchFilterIfAny();
            //Builds filter string according to the filter chosen in the grid's columns filter
            var fieldsFilter = buildFilterStringIfAny(); 

            //The final 'where' string
            var forStep = orderItemsGridSharedSettings.getStepFilter() || "all-open";
            var whereString;
            if (forStep === 'all-open') {
                //we're querying over orderItems ignoring specified in config steps plus adding dates filter if specified so
                //(however, we ignore stepFilter if datesFilter was specified to filter by completion date )
                //plus to be safe and not have empty 'where' clause in case we don't have ignored steps and dates filter,
                //we specify that we want only not deleted items
                whereString =
                    'OrderItem.Deleted = false' +
                    (stepsFilter.length > 0 && (!datesFilter || datesFilter.length === 0) ? ' AND ' + stepsFilter : '') +
                    (datesFilter.length > 0 ? ' AND ' + datesFilter : '') +
                    (fieldsFilter.length > 0 ? ' AND ' + fieldsFilter : '') +
                    (textSearchFilter.length > 0 ? ' AND ' + textSearchFilter : '');
            }
            else {
                //we're querying over orderItems at particular step.
                //we want to show non deleted and non completed items at that step
                //datesFilter string will contain the filter for dates fields to include non completed only
                whereString =
                    'OrderItem.Deleted = false' +
                    (forStep ? _.string.sprintf(" AND Step.Code = '%s'", forStep) : '') +
                    (datesFilter.length > 0 ? ' AND ' + datesFilter : '') +
                    (fieldsFilter.length > 0 ? ' AND ' + fieldsFilter : '') +
                    (textSearchFilter.length > 0 ? ' AND ' + textSearchFilter : '');
            }

            return whereString;
        }

        function buildFilterStringIfAny() {
            var filterByFields = orderItemsGridSharedSettings.getGridFilter();

            var parseFilterDescriptor = function (fDescriptor) {
                var filterString = "";
                var logic = fDescriptor.logic;
                var filters = fDescriptor.filters;
                if (filters) {
                    //Iterate over filters 
                    filterString += " (";
                    for (var i = 0; i < filters.length; i++) {
                        if (!filters[i]) continue;
                        var filter = filters[i];
                        if (filter.filters) {
                            //parse filters collection recursively
                            filterString += parseFilterDescriptor(filter);
                        } else {
                            //parse single filter descriptor for the field
                            var omqlField = omqlAliasBuilder.mapFieldToOrderItemAlias(filter.field);
                            if (_.string.contains(omqlField, "undefined.")) {
                                //Just warn user about what columns will be ignored in filtering
                                toastr.warning(_.str.sprintf("The column '%s' does not support filtering",
                                    _.str(filter.field).strRightBack(".").capitalize().value()), gettextCatalog.getString("Please try again"));
                            } else {
                                if (i > 0) {
                                    filterString += _.str.sprintf(" %s ", logic);
                                }

                                //Get omql query according to the specified logic operator (=, <, >, etc.), field name and value and its type
                                filterString += omqlAliasBuilder.buildOperatorQuery(filter.operator, omqlField, filter.value);
                            }
                        }
                    }
                    filterString += ") ";
                }
                return filterString;
            };

            if (!filterByFields || !filterByFields.filters || filterByFields.filters.length === 0) return "";
            
            return parseFilterDescriptor(filterByFields);
        }

        function buildSortString() {
            var sortByFields = orderItemsGridSharedSettings.getGridSort();

            var sortString = "";
            //Apply default sorting by date of creation if no user-specific sorting applied
            if (!sortByFields || sortByFields.length === 0) return "OrderItem.Created desc";

            for (var i = 0; i < sortByFields.length; i++) {
                if (!sortByFields[i]) continue;
                var field = omqlAliasBuilder.mapFieldToOrderItemAlias(sortByFields[i].field);
                if (_.string.contains(field, "undefined.")) {
                    //Just warn user about what columns will be ignored in sorting
                    toastr.warning(
                        _.str.sprintf("The column '%s' does not support sorting",
                            _.str(sortByFields[i].field).strRightBack(".").capitalize().value()),
                        gettextCatalog.getString("Please try again"));
                } else {
                    var direction = sortByFields[i].dir;
                    sortString += _.string.sprintf("%s %s, ", field, direction);
                }
            }

            return _.string.rtrim(sortString, ", ");
        }

        function buildDatesFilterIfAny() {
            var completedFilter = orderItemsGridSharedSettings.getCompletedFilter();

            var filterString = '';
            if (completedFilter && completedFilter.enabled && completedFilter.from && completedFilter.to) {
                //We're showing completed orders/orderItems, so build the filter for dates when items were
                //marked as completed. And set 'from' to the beginning of the day and 'to' to the end of the day
                completedFilter.from.setHours(0);
                completedFilter.from.setMinutes(0);
                completedFilter.from.setSeconds(0);
                completedFilter.from.setMilliseconds(0);
                completedFilter.to.setHours(23);
                completedFilter.to.setMinutes(59);
                completedFilter.to.setSeconds(59);
                completedFilter.to.setMilliseconds(999);

                //we need timestamp without the milliseconds part, so omql api will understand it
                //OMQL DateTimeFormat = "yyyy-MM-ddTHH:mm:ss". i.e.: AND OrderItem.Completed = #0001-01-01T00:00:00#
                filterString = _.string.sprintf("OrderItem.Completed >= #%s# AND OrderItem.Completed <= #%s#",
                    completedFilter.from.toISOString(), completedFilter.to.toISOString());
            } else {
                //Otherwise we don't need to build a dates filtering string to ignore completed items
                filterString = '';
            }
            return filterString;
        }

        function buildTextSearchFilterIfAny() {
            var textFilter = orderItemsGridSharedSettings.getTextSearch();

            if (!textFilter || !textFilter.text || textFilter.text.length < 1) {
                return "";
            }
            var criteria = gridTextSearchService.getCriteria(textFilter.text, !!textFilter.exact);
            if (criteria.length > 0) {
                return "(" + criteria.join(" OR ") + ")";
            }
            return "";
        }

        function buildStepsFilterForIgnoredSteps() {
            var ignoredSteps = _.where($customisationConfig.workflow.steps, {ignore: true});

            return _
                .map(ignoredSteps, function (s) {
                    return _.string.sprintf("Step.Code <> '%s'", s.code);
                })
                .join(" AND ");
        }

    })

;